import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  PiArrowCounterClockwise,
  PiBookOpenText,
  PiCards,
  PiCheckBold,
  PiEye,
  PiLightbulbFill,
  PiSpeakerHigh,
  PiWarningFill,
  PiXBold,
} from "react-icons/pi";
import { fillPlaceholders, lstr } from "./localization";
import { StoryMultilingual } from "./story";
import { storyQueryKey } from "./queries";
import getFlagEmoji from "./LanguageFlag";
import { GENDER_BY_NAME, GENDER_CLASS, supportsGenderColoring } from "./gender";
import {
  GenderNoun,
  Grade,
  PracticeCard,
  PracticeStatus,
  fetchGenderRound,
  fetchNextCards,
  fetchPracticeStatus,
  learnMore,
  practiceAudioUrl,
  submitAnswer,
} from "./practiceApi";
import { PlacementGame, PlacementOffer } from "./PlacementGame";
import { GenderRound, GENDER_ROUND_LANGUAGES } from "./GenderRound";
import { ProgressRing } from "./ProgressRing";
import { SkeletonBlock } from "./Skeleton";
import { Pup } from "./Pup";
import { Confetti } from "./Confetti";

// Cards kept ready in the queue, so the next one shows instantly.
const QUEUE_TARGET = 3;
// A der/die/das round comes after this many answers.
const GENDER_ROUND_EVERY = 10;
const AUTOPLAY_KEY = "practiceAutoplay";

// Each card in the queue gets its own id: the same word can come back later
// in a session, and its card view must start fresh then.
interface QueuedCard {
  uid: number;
  card: PracticeCard;
}

type Phase =
  | "loading"
  | "error"
  | "placement-offer"
  | "placement"
  | "cards"
  | "gender"
  | "done";

function wordClass(card: PracticeCard, r: string) {
  const gender = GENDER_BY_NAME[card.gender];
  return gender && card.partOfSpeech === "noun" && supportsGenderColoring(r)
    ? GENDER_CLASS[gender]
    : "text-main-text";
}

// newReviewId makes the id that lets the server count a retried answer once.
// crypto.randomUUID is missing in older Safari.
function newReviewId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

function loadAutoplay(): boolean {
  try {
    return localStorage.getItem(AUTOPLAY_KEY) === "1";
  } catch {
    return false;
  }
}

function playAudio(source: { entryId: number } | { sentenceId: number }) {
  practiceAudioUrl(source)
    .then((url) => new Audio(url).play())
    .catch((err) => console.error("Failed to play practice audio", err));
}

// normalizeAnswer makes typed answers comparable: case, spacing,
// punctuation and ß/ss don't matter.
function normalizeAnswer(text: string) {
  return text
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/[.,!?;:"'()«»„“…]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// typedAnswerMatches accepts the answer itself, a noun without its article,
// and for meanings any of the listed alternatives, with or without a
// leading "to"/"the"/"a".
function typedAnswerMatches(card: PracticeCard, typed: string) {
  const answer = normalizeAnswer(typed);
  if (!answer) return false;
  const candidates =
    card.direction === "produce"
      ? [card.answer, card.answer.replace(/^(der|die|das) /i, "")]
      : card.answer.split(/[;,/]/);
  return candidates.some((candidate) => {
    const normalized = normalizeAnswer(candidate);
    const bare = normalized.replace(/^(to|the|a|an) /, "");
    return answer === normalized || answer === bare;
  });
}

// withGap hides the first occurrence of part in text behind a gap.
function withGap(text: string, part: string) {
  const index = part ? text.indexOf(part) : -1;
  return index < 0
    ? text
    : `${text.slice(0, index)}_____${text.slice(index + part.length)}`;
}

// Highlight shows text with the first occurrence of part emphasized.
function Highlight({
  text,
  part,
  className,
}: {
  text: string;
  part: string;
  className: string;
}) {
  const index = part ? text.indexOf(part) : -1;
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <span className={className}>{part}</span>
      {text.slice(index + part.length)}
    </>
  );
}

function ListenButton({
  l,
  source,
}: {
  l: string;
  source: { entryId: number } | { sentenceId: number };
}) {
  return (
    <button
      type="button"
      onClick={() => playAudio(source)}
      aria-label={lstr(l).listen_button}
      title={lstr(l).listen_button}
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary transition-[background-color,transform] hover:bg-primary/15 active:scale-95"
    >
      <PiSpeakerHigh size={18} />
    </button>
  );
}

function PartOfSpeech({ partOfSpeech }: { partOfSpeech: string }) {
  if (partOfSpeech === "other" || partOfSpeech === "interjection") return null;
  return (
    <span className="rounded-full bg-cream-dark px-2.5 py-0.5 text-xs font-medium text-secondary-text">
      {partOfSpeech}
    </span>
  );
}

// The card's shortcuts leave a key to the focused element that handles it:
// Enter and Space to any control, every other key to a text field.
const CONTROL_ELEMENTS =
  "button, a, input, textarea, select, [contenteditable]";
const TEXT_FIELD_ELEMENTS = "input, textarea, select, [contenteditable]";

// Fixed: one card is shown at a time.
const CARD_PROMPT_ID = "practice-card-prompt";

function CardView({
  card,
  l,
  r,
  autoplay,
  onGrade,
}: {
  card: PracticeCard;
  l: string;
  r: string;
  autoplay: boolean;
  onGrade: (grade: Grade, hintsUsed: number) => void;
}) {
  const strings = lstr(l);
  const [hintsShown, setHintsShown] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typed, setTyped] = useState("");
  const isRecognize = card.direction === "recognize";
  const wordAudio = { entryId: card.entryId };
  const sentenceAudio = card.sentenceId
    ? { sentenceId: card.sentenceId }
    : null;

  // Recognize cards show the learned language right away; produce cards
  // only once revealed.
  useEffect(() => {
    if (!autoplay) return;
    if (isRecognize) {
      playAudio(
        card.format === "recognize_sentence" && sentenceAudio
          ? sentenceAudio
          : wordAudio,
      );
    } else if (revealed) {
      playAudio(
        card.format === "produce_sentence" && sentenceAudio
          ? sentenceAudio
          : wordAudio,
      );
    }
    // Only when the card shows up and when it's revealed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  const grades: { grade: Grade; label: string; className: string }[] = [
    {
      grade: 1,
      label: strings.practice_grade_forgot,
      className:
        "border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-400/40 dark:text-rose-300 dark:hover:bg-rose-400/10",
    },
    {
      grade: 2,
      label: strings.practice_grade_hard,
      className:
        "border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-400/40 dark:text-amber-300 dark:hover:bg-amber-400/10",
    },
    {
      grade: 3,
      label: strings.practice_grade_medium,
      className:
        "border-sky-300 text-sky-700 hover:bg-sky-50 dark:border-sky-400/40 dark:text-sky-300 dark:hover:bg-sky-400/10",
    },
    {
      grade: 4,
      label: strings.practice_grade_easy,
      className:
        "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-400/40 dark:text-emerald-300 dark:hover:bg-emerald-400/10",
    },
  ];
  // Answers found with a hint count as hard at best.
  const availableGrades = hintsShown > 0 ? grades.slice(0, 2) : grades;

  const showHint = () =>
    setHintsShown((shown) => Math.min(shown + 1, card.hints.length));

  // A new card replaces the old one, and revealing removes the controls
  // used to answer; either way keyboard focus would fall back to the page,
  // so it moves to the card itself, where the shortcuts work, and a screen
  // reader announces the card's prompt. Focus on anything else that is still
  // there stays put.
  const cardRef = useRef<HTMLDivElement>(null);
  const answerInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!document.activeElement || document.activeElement === document.body) {
      cardRef.current?.focus({ preventScroll: true });
    }
  }, [revealed]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const inAnswerInput =
        target !== null && target === answerInputRef.current;
      if (event.key === "Enter" || event.key === " ") {
        // Only the answer input's Enter is the card's; a focused button
        // (Hint, autoplay) or link acts on its own.
        const handledByTarget = inAnswerInput
          ? event.key === " "
          : target?.closest(CONTROL_ELEMENTS);
        if (!revealed && !handledByTarget) {
          event.preventDefault();
          setRevealed(true);
        }
        return;
      }
      if (target?.closest(TEXT_FIELD_ELEMENTS)) return;
      if (!revealed && event.key.toLowerCase() === "h") {
        showHint();
      } else if (revealed) {
        const grade = availableGrades.find(
          (g) => g.grade.toString() === event.key,
        );
        if (grade) onGrade(grade.grade, hintsShown);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const typedCorrect = typed.trim() !== "" && typedAnswerMatches(card, typed);

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      role="group"
      aria-labelledby={CARD_PROMPT_ID}
      className="card animate-pop-in w-full origin-bottom rounded-3xl p-5 outline-none sm:p-7"
    >
      <div className="mb-2 flex items-center gap-2 text-xs text-secondary-text">
        {card.isNew && (
          <span className="rounded-full bg-primary-light px-2.5 py-1 font-semibold text-primary">
            {strings.practice_new_word}
          </span>
        )}
        {card.leech && (
          <span className="inline-flex items-center gap-1 rounded-full bg-highlight-light px-2.5 py-1 font-semibold text-amber-700 dark:text-amber-300">
            <PiWarningFill size={12} /> {strings.practice_tricky_word}
          </span>
        )}
        <span id={CARD_PROMPT_ID} className="ml-auto text-right">
          {card.format === "recognize_word" &&
            strings.practice_prompt_recognize_word}
          {card.format === "recognize_sentence" &&
            strings.practice_prompt_recognize_sentence}
          {card.format === "produce_word" &&
            `${strings.practice_prompt_produce_word} ${getFlagEmoji(r)}`}
          {card.format === "produce_sentence" &&
            `${strings.practice_prompt_produce_sentence} ${getFlagEmoji(r)}`}
        </span>
      </div>

      {/* Prompt */}
      {card.format === "recognize_word" && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-7">
          <span
            lang={r}
            className={`font-literata text-[34px] font-bold leading-tight sm:text-[40px] ${wordClass(card, r)}`}
          >
            {card.word}
          </span>
          <PartOfSpeech partOfSpeech={card.partOfSpeech} />
          <ListenButton l={l} source={wordAudio} />
        </div>
      )}
      {card.format === "recognize_sentence" && (
        <div className="flex items-start justify-center gap-3 py-6">
          <p
            lang={r}
            className="font-literata text-[21px] leading-relaxed text-main-text text-center sm:text-[23px]"
          >
            <Highlight
              text={card.sentence}
              part={card.target}
              className="font-bold text-primary"
            />
          </p>
          {sentenceAudio && <ListenButton l={l} source={sentenceAudio} />}
        </div>
      )}
      {card.format === "produce_word" && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-7">
          <span
            lang={l}
            className="font-literata text-[30px] font-bold leading-tight text-main-text text-center sm:text-[34px]"
          >
            {card.meaning}
          </span>
          <PartOfSpeech partOfSpeech={card.partOfSpeech} />
        </div>
      )}
      {card.format === "produce_sentence" && (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p
            lang={r}
            className="font-literata text-[21px] leading-relaxed text-main-text sm:text-[23px]"
          >
            {revealed ? (
              <Highlight
                text={card.sentence}
                part={card.target}
                className="font-bold text-primary"
              />
            ) : (
              withGap(card.sentence, card.target)
            )}
          </p>
          <p lang={l} className="text-secondary-text">
            <Highlight
              text={card.translation}
              part={card.translationTarget}
              className="font-semibold text-main-text"
            />
          </p>
        </div>
      )}

      {/* Hints */}
      {hintsShown > 0 && (
        <ul className="mb-4 flex flex-col gap-2 rounded-2xl bg-highlight-light/60 px-4 py-3">
          {card.hints.slice(0, hintsShown).map((hint, index) => (
            <li
              key={index}
              // The first hint masks the answer to its first letters: spaced
              // out, so the letters can be counted.
              className={`flex items-start gap-2 ${index === 0 ? "font-mono tracking-[0.3em] text-base text-main-text" : "text-sm text-secondary-text"}`}
            >
              <PiLightbulbFill
                size={14}
                className="mt-1 shrink-0 text-amber-500"
              />
              {/* Recognize hints: the meaning's letters and the translation
                  are in the spoken language, the definition isn't. */}
              <span lang={isRecognize && index < 2 ? l : r}>{hint}</span>
            </li>
          ))}
        </ul>
      )}

      {!revealed && (
        <>
          <input
            ref={answerInputRef}
            type="text"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            lang={isRecognize ? l : r}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={strings.practice_type_placeholder}
            className="input mb-3 bg-cream px-4 py-3 text-center text-[16px]"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={hintsShown >= card.hints.length}
              onClick={showHint}
              className="btn-secondary px-4 py-3 font-medium"
            >
              <PiLightbulbFill size={16} className="text-amber-500" />
              {strings.practice_hint}
              {card.hints.length > 0 && (
                <span className="text-xs tabular-nums text-secondary-text">
                  {hintsShown}/{card.hints.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="btn-primary flex-grow px-4 py-3"
            >
              <PiEye size={18} />
              {strings.practice_reveal}
            </button>
          </div>
        </>
      )}

      {revealed && (
        <>
          <div className="animate-enter flex flex-col gap-2.5 border-t border-border pt-5">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span
                lang={isRecognize ? l : r}
                className={`font-literata text-[26px] font-bold leading-tight text-center ${isRecognize ? "text-main-text" : wordClass(card, r)}`}
              >
                {card.answer}
              </span>
              {!isRecognize && <ListenButton l={l} source={wordAudio} />}
            </div>
            {card.formNote && card.format !== "recognize_word" && (
              <p className="text-center text-sm text-secondary-text">
                <span lang={r} className="font-semibold">
                  {card.target}
                </span>{" "}
                — {card.formNote}
              </p>
            )}
            {card.forms.length > 0 && (
              <p
                lang={r}
                className={`text-center font-medium ${wordClass(card, r)}`}
              >
                {card.forms.map((form, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <span className="text-secondary-text"> · </span>
                    )}
                    {form}
                  </Fragment>
                ))}
              </p>
            )}
            {isRecognize &&
              card.format === "recognize_sentence" &&
              card.translation && (
                <p lang={l} className="text-center text-sm text-secondary-text">
                  <Highlight
                    text={card.translation}
                    part={card.translationTarget}
                    className="font-semibold text-main-text"
                  />
                </p>
              )}
            {card.mnemonic && (
              <p className="flex items-start gap-2 rounded-2xl bg-highlight-light p-3.5 text-sm text-main-text">
                <PiLightbulbFill
                  size={15}
                  className="mt-0.5 shrink-0 text-amber-500"
                />
                <span lang={l}>{card.mnemonic}</span>
              </p>
            )}
            {typed.trim() !== "" && (
              <p
                className={`flex items-center justify-center gap-1.5 text-sm ${typedCorrect ? "text-emerald-600 dark:text-emerald-400" : "text-error"}`}
              >
                {typedCorrect ? (
                  <PiCheckBold size={13} />
                ) : (
                  <PiXBold size={13} />
                )}
                {typedCorrect
                  ? strings.practice_typed_correct
                  : fillPlaceholders(strings.practice_typed_wrong, { typed })}
              </p>
            )}
          </div>
          <div
            className={`mt-5 grid gap-1.5 sm:gap-2 ${availableGrades.length === 4 ? "grid-cols-4" : "grid-cols-2"}`}
          >
            {availableGrades.map(({ grade, label, className }) => (
              <button
                key={grade}
                type="button"
                onClick={() => onGrade(grade, hintsShown)}
                className={`btn relative flex-col gap-0.5 rounded-2xl border-2 bg-surface py-3 text-[13px] sm:text-sm ${className}`}
              >
                {label}
                <kbd className="hidden font-sans text-[10px] font-medium opacity-60 sm:block">
                  {grade}
                </kbd>
              </button>
            ))}
          </div>
          {hintsShown > 0 && (
            <p className="mt-2 text-center text-xs text-secondary-text">
              {strings.practice_hint_grade_note}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// StatusBar shows where today's practice stands: the ring fills with the
// day's new words against the goal, next to what's waiting for review.
function StatusBar({
  l,
  status,
  storyId,
  autoplay,
  setAutoplay,
}: {
  l: string;
  status: PracticeStatus;
  // The story of a story session, "" otherwise.
  storyId: string;
  autoplay: boolean;
  setAutoplay: (value: boolean) => void;
}) {
  const strings = lstr(l);
  const chipClass =
    "inline-flex items-center rounded-full bg-cream-dark px-3 py-1 text-xs font-medium text-secondary-text";
  return (
    <div className="flex items-center gap-3">
      <ProgressRing
        value={status.newToday / Math.max(1, status.dailyGoal)}
        size={44}
        strokeWidth={4}
      >
        <span className="text-[11px] font-bold tabular-nums text-main-text">
          {status.newToday}/{status.dailyGoal}
        </span>
      </ProgressRing>
      <div className="flex min-w-0 flex-grow flex-wrap items-center gap-1.5">
        {/* The due count is of all the user's words, not the story's. */}
        {!storyId && (
          <span className={chipClass}>
            {fillPlaceholders(strings.practice_status_due, {
              count: status.due,
            })}
          </span>
        )}
        <span className={chipClass}>
          {fillPlaceholders(strings.practice_status_new, {
            done: status.newToday,
            goal: status.dailyGoal,
          })}
        </span>
        <span className={chipClass}>
          {fillPlaceholders(strings.practice_status_level, {
            level: status.wordLevel,
          })}
        </span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={autoplay}
        aria-label={strings.practice_autoplay}
        title={strings.practice_autoplay}
        onClick={() => setAutoplay(!autoplay)}
        className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-colors ${
          autoplay
            ? "bg-primary-light text-primary"
            : "text-secondary-text hover:bg-cream-dark"
        }`}
      >
        <PiSpeakerHigh size={16} />
        <span className="hidden sm:inline">{strings.practice_autoplay}</span>
      </button>
    </div>
  );
}

function formatNextDue(l: string, nextDueAt: Date | null) {
  if (!nextDueAt) return null;
  const minutes = Math.max(
    1,
    Math.round((nextDueAt.getTime() - Date.now()) / 60000),
  );
  const strings = lstr(l);
  if (minutes < 60)
    return fillPlaceholders(strings.practice_next_due_minutes, {
      count: minutes,
    });
  if (minutes < 48 * 60)
    return fillPlaceholders(strings.practice_next_due_hours, {
      count: Math.round(minutes / 60),
    });
  return fillPlaceholders(strings.practice_next_due_days, {
    count: Math.round(minutes / (24 * 60)),
  });
}

function DoneView({
  l,
  status,
  storyId,
  onLearnMore,
  onContinue,
  busy,
  celebrate,
}: {
  l: string;
  status: PracticeStatus;
  // The story of a story session, "" otherwise.
  storyId: string;
  onLearnMore: () => void;
  onContinue: () => void;
  busy: boolean;
  // True when the learner just got here by answering cards.
  celebrate: boolean;
}) {
  const strings = lstr(l);
  // The next review may be of a word from another story.
  const nextDue = storyId ? null : formatNextDue(l, status.nextDueAt);
  let title = strings.practice_done_caught_up;
  let text = "";
  switch (status.paused) {
    case "goal_reached":
      title = strings.practice_done_goal_title;
      text = fillPlaceholders(strings.practice_done_goal_text, {
        count: status.newToday,
      });
      break;
    case "daily_cap":
      text = fillPlaceholders(strings.practice_done_cap_text, {
        count: status.newToday,
      });
      break;
    case "struggling":
      text = strings.practice_done_struggling_text;
      break;
    case "consolidating":
      text = strings.practice_done_consolidating_text;
      break;
  }
  if (storyId) {
    title = strings.practice_story_done_title;
    text = strings.practice_story_done_text;
  }
  // Learning more and checking again would leave the story's words.
  const canLearnMore = !storyId && status.canLearnMore;
  const canCheckAgain =
    !storyId && (status.paused === "consolidating" || status.paused === "");
  const statChipClass =
    "rounded-full bg-primary-light px-3 py-1 text-xs font-semibold text-primary";
  return (
    <div className="card animate-pop-in flex w-full flex-col items-center gap-3 rounded-3xl px-6 py-9 text-center">
      <div className="relative">
        <Pup shape="circle" mood="happy" className="h-24 w-24" />
        {celebrate && <Confetti />}
      </div>
      <h2 className="mt-2 font-literata text-[26px] font-bold text-main-text">
        {title}
      </h2>
      {text && <p className="max-w-md text-main-text">{text}</p>}
      <div className="flex flex-wrap justify-center gap-1.5">
        <span className={statChipClass}>
          {fillPlaceholders(strings.practice_done_reviews, {
            count: status.reviewsToday,
          })}
        </span>
        <span className={statChipClass}>
          {fillPlaceholders(strings.practice_done_new_words, {
            count: status.newToday,
          })}
        </span>
      </div>
      {nextDue && <p className="text-sm text-secondary-text">{nextDue}</p>}
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        {canLearnMore && (
          <button
            type="button"
            disabled={busy}
            onClick={onLearnMore}
            className="btn-primary px-5 py-3"
          >
            {fillPlaceholders(strings.practice_learn_more, {
              count: status.learnMoreSize,
            })}
          </button>
        )}
        {canCheckAgain && (
          <button
            type="button"
            disabled={busy}
            onClick={onContinue}
            className="btn-secondary px-5 py-3"
          >
            <PiArrowCounterClockwise size={16} />
            {strings.practice_check_again}
          </button>
        )}
        {storyId && (
          <Link to="/practice" className="btn-primary px-5 py-3">
            <PiCards size={17} />
            {strings.practice_story_continue}
          </Link>
        )}
        <Link
          to={storyId ? `/${encodeURIComponent(storyId)}` : "/"}
          className="btn-secondary px-5 py-3"
        >
          <PiBookOpenText size={17} className="text-primary" />
          {storyId ? strings.practice_story_back : strings.practice_read_story}
        </Link>
      </div>
    </div>
  );
}

// PracticeSkeleton holds the card's place while the next cards load.
function PracticeSkeleton() {
  return (
    <div
      className="card flex w-full flex-col items-center gap-4 rounded-3xl p-7"
      aria-hidden="true"
    >
      <SkeletonBlock className="h-4 w-24 self-end" />
      <SkeletonBlock className="my-6 h-10 w-48" />
      <SkeletonBlock className="h-12 w-full rounded-xl" />
      <div className="flex w-full gap-2">
        <SkeletonBlock className="h-12 w-28 rounded-xl" />
        <SkeletonBlock className="h-12 flex-grow rounded-xl" />
      </div>
    </div>
  );
}

export function PracticeView({ l, r }: { l: string; r: string }) {
  // /practice?story=<id> is a story session: only the words the user met in
  // that story. Fresh session state per story.
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get("story") ?? "";
  return <PracticeSession key={storyId} l={l} r={r} storyId={storyId} />;
}

function PracticeSession({
  l,
  r,
  storyId,
}: {
  l: string;
  r: string;
  // The story of a story session, "" otherwise.
  storyId: string;
}) {
  const strings = lstr(l);
  const queryClient = useQueryClient();
  // The story's title when the story is cached (see useStoryQuery), as when
  // coming from it; not worth loading the whole story for.
  const storyTitle = storyId
    ? queryClient
        .getQueryData<StoryMultilingual>(storyQueryKey(storyId, l, r))
        ?.localizations.get(r)?.title
    : undefined;
  const [phase, setPhase] = useState<Phase>("loading");
  const [queue, setQueueState] = useState<QueuedCard[]>([]);
  const [status, setStatus] = useState<PracticeStatus | null>(null);
  const [autoplay, setAutoplayState] = useState(loadAutoplay);
  const [busy, setBusy] = useState(false);
  const [answerFailed, setAnswerFailed] = useState(false);
  const [genderNouns, setGenderNouns] = useState<GenderNoun[]>([]);

  const queueRef = useRef<QueuedCard[]>([]);
  const nextUid = useRef(0);
  const answered = useRef(0);
  // Answers and card fetches run one after another, so a fetch always sees
  // the answers before it.
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const genderRound = useRef<Promise<GenderNoun[]> | null>(null);
  // Responses from a previous language pair are dropped.
  const generation = useRef(0);

  const setQueue = (cards: QueuedCard[]) => {
    queueRef.current = cards;
    setQueueState(cards);
  };

  // Keep the cached status (the due count on the Practice tab, the home
  // page's practice card) in step with the session.
  useEffect(() => {
    if (status) queryClient.setQueryData(["practice-status", r], status);
  }, [status, r, queryClient]);

  const setAutoplay = (value: boolean) => {
    setAutoplayState(value);
    try {
      localStorage.setItem(AUTOPLAY_KEY, value ? "1" : "0");
    } catch {
      // Not remembered; fine.
    }
  };

  // refill fetches cards until the queue holds QUEUE_TARGET, after any
  // pending answers. It resolves with the number of cards added.
  const refill = useCallback((): Promise<number> => {
    const current = generation.current;
    const run = chain.current.then(async () => {
      const queued = queueRef.current;
      if (queued.length >= QUEUE_TARGET) return 0;
      const result = await fetchNextCards(
        l,
        r,
        queued.map((q) => q.card),
        QUEUE_TARGET - queued.length,
        storyId,
      );
      if (generation.current !== current) return 0;
      setStatus(result.status);
      const known = new Set(
        queueRef.current.map((q) => `${q.card.entryId}:${q.card.direction}`),
      );
      const added = result.cards
        .filter((card) => !known.has(`${card.entryId}:${card.direction}`))
        .map((card) => ({ uid: nextUid.current++, card }));
      setQueue([...queueRef.current, ...added]);
      return added.length;
    });
    chain.current = run.catch(() => undefined);
    return run;
  }, [l, r, storyId]);

  const startCards = useCallback(() => {
    setPhase("loading");
    refill()
      .then(() => setPhase(queueRef.current.length > 0 ? "cards" : "done"))
      .catch(() => setPhase("error"));
  }, [refill]);

  useEffect(() => {
    generation.current++;
    setQueue([]);
    setStatus(null);
    answered.current = 0;
    genderRound.current = null;
    chain.current = Promise.resolve();
    setPhase("loading");
    const current = generation.current;
    // The placement game is offered before any new word is picked at a
    // guessed level. A story session's new words come from the story.
    if (storyId) {
      startCards();
      return;
    }
    fetchPracticeStatus(r)
      .then((initialStatus) => {
        if (generation.current !== current) return;
        setStatus(initialStatus);
        if (initialStatus.levelSource === "default") {
          setPhase("placement-offer");
        } else {
          startCards();
        }
      })
      .catch(() => {
        if (generation.current === current) setPhase("error");
      });
    // startCards changes with l, r and storyId, as does this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l, r, storyId]);

  const handleGrade = (grade: Grade, hintsUsed: number) => {
    const [answeredCard, ...rest] = queueRef.current;
    if (!answeredCard) return;
    const reviewId = newReviewId();
    const send = async () => {
      // A retry reuses the review id, so it counts once.
      for (let attempt = 0; ; attempt++) {
        try {
          await submitAnswer(
            l,
            r,
            answeredCard.card,
            grade,
            hintsUsed,
            reviewId,
          );
          setAnswerFailed(false);
          return;
        } catch (err) {
          if (attempt === 2) throw err;
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * (attempt + 1)),
          );
        }
      }
    };
    chain.current = chain.current.then(send).catch(() => setAnswerFailed(true));
    setQueue(rest);
    answered.current++;

    // Gender rounds draw from all the user's nouns, not the story's.
    if (!storyId && GENDER_ROUND_LANGUAGES.has(r)) {
      if (answered.current % GENDER_ROUND_EVERY === GENDER_ROUND_EVERY - 3) {
        genderRound.current = fetchGenderRound(l, r).catch(() => []);
      }
      if (answered.current % GENDER_ROUND_EVERY === 0 && genderRound.current) {
        const round = genderRound.current;
        genderRound.current = null;
        round.then((nouns) => {
          if (nouns.length > 0) {
            setGenderNouns(nouns);
            setPhase("gender");
          }
        });
      }
    }

    refill()
      .then(() => {
        if (queueRef.current.length === 0)
          setPhase((phase) => (phase === "gender" ? phase : "done"));
      })
      .catch(() => {
        if (queueRef.current.length === 0) setPhase("error");
      });
  };

  const handleLearnMore = () => {
    setBusy(true);
    learnMore(r)
      .then((newStatus) => {
        setStatus(newStatus);
        startCards();
      })
      .catch(() => setPhase("error"))
      .finally(() => setBusy(false));
  };

  const current = queue[0];

  return (
    <div className="flex w-full flex-col gap-5 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-literata text-[28px] font-bold leading-tight text-main-text">
          {strings.practice_header}
        </h1>
        <Link
          to="/dictionary?filter=all"
          className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
        >
          {strings.practice_all_words_link}
        </Link>
      </div>
      {storyId && (
        <p className="-mt-3 flex items-center gap-2 text-sm text-secondary-text">
          <PiBookOpenText size={16} className="shrink-0 text-primary" />
          <span className="min-w-0 truncate">
            {strings.practice_story_header}
            {storyTitle && (
              <>
                {": "}
                <span lang={r} className="font-semibold text-main-text">
                  {storyTitle}
                </span>
              </>
            )}
          </span>
        </p>
      )}
      {status &&
        (phase === "cards" || phase === "done" || phase === "gender") && (
          <StatusBar
            l={l}
            status={status}
            storyId={storyId}
            autoplay={autoplay}
            setAutoplay={setAutoplay}
          />
        )}
      {answerFailed && (
        <p role="alert" className="text-sm text-error">
          {strings.practice_answer_error}
        </p>
      )}

      {phase === "loading" && (
        <>
          <span className="sr-only">{strings.practice_loading}</span>
          <PracticeSkeleton />
        </>
      )}
      {phase === "error" && (
        <div className="card flex flex-col items-center gap-4 rounded-3xl px-6 py-9 text-center">
          <Pup shape="circle" className="h-16 w-16" />
          <p className="text-main-text">{strings.practice_error}</p>
          <button
            type="button"
            onClick={startCards}
            className="btn-secondary px-5 py-2.5"
          >
            <PiArrowCounterClockwise size={16} />
            {strings.practice_check_again}
          </button>
        </div>
      )}
      {phase === "placement-offer" && (
        <PlacementOffer
          l={l}
          onStart={() => setPhase("placement")}
          onSkip={startCards}
          r={r}
        />
      )}
      {phase === "placement" && (
        <PlacementGame l={l} r={r} onDone={startCards} />
      )}
      {phase === "gender" && (
        <GenderRound
          l={l}
          r={r}
          nouns={genderNouns}
          onDone={() =>
            setPhase(queueRef.current.length > 0 ? "cards" : "done")
          }
        />
      )}
      {phase === "cards" &&
        (current ? (
          <CardView
            key={current.uid}
            card={current.card}
            l={l}
            r={r}
            autoplay={autoplay}
            onGrade={handleGrade}
          />
        ) : (
          <PracticeSkeleton />
        ))}
      {phase === "done" && status && (
        <DoneView
          l={l}
          status={status}
          storyId={storyId}
          onLearnMore={handleLearnMore}
          onContinue={startCards}
          busy={busy}
          celebrate={answered.current > 0}
        />
      )}
      {phase === "cards" && (
        <p className="hidden text-center text-xs text-secondary-text sm:block">
          {strings.practice_keyboard_help}
        </p>
      )}
    </div>
  );
}
